// @ai-generated 2026-08-18T00:00:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
import { AGGREGATE_FUNCTION_LABEL, lookupField } from '../fieldMetadata';
import type { SynqoOperand } from '@Model';
import { formatLiteral } from './formatLiteral';
// Import cycle on purpose: a filter's leaf operands come back through OperandRenderer. Safe because
// both sides are hoisted function declarations called only at render time, and it mirrors the
// ConditionVisitor ⇄ renderer cycle the visitor pattern already relies on.
import { InlinePredicate } from './InlinePredicate';
import styles from './OperandRenderer.module.css';

interface OperandRendererProps {
    operand: SynqoOperand;
}

/**
 * Renders one value position of a condition. Shared by compare (which has two) and predicate
 * (which has one), so the four operand kinds are styled identically wherever they appear:
 * literals as a filled chip, everything else as plain field-style text.
 */
export function OperandRenderer({ operand }: OperandRendererProps) {
    switch (operand.kind) {
        case 'literal':
            return <span className={styles.literal}>{formatLiteral(operand.value)}</span>;

        case 'field':
            return <span className={styles.value}>{lookupField(operand.path)?.label ?? operand.path}</span>;

        case 'aggregate': {
            const functionLabel = AGGREGATE_FUNCTION_LABEL[operand.function];
            const collectionLabel = lookupField(operand.collection)?.label ?? operand.collection;
            // `Count` folds no field, so it renders as "Anzahl(Positionen)" with nothing inside.
            const fieldLabel = operand.field ? lookupField(operand.field)?.label ?? operand.field : undefined;

            return (
                <span className={styles.aggregate}>
                    <span className={styles.aggregateFunction}>{functionLabel}</span>
                    <span className={styles.punctuation}>(</span>
                    {collectionLabel}
                    {/* The filter is spelled out rather than badged: a rule the reader cannot see is a
                        rule they cannot check. Safe to render inline because the backend restricts a
                        filter to a simple predicate, which is always a short phrase. */}
                    {operand.where !== undefined && (
                        <span className={styles.filter}>
                            <span className={styles.filterLead}>mit</span>
                            <InlinePredicate node={operand.where} />
                        </span>
                    )}
                    {fieldLabel !== undefined && (
                        <>
                            {/* Margins, not literal spaces: this is a flex row, which collapses a text
                                node's leading and trailing whitespace. */}
                            <span className={styles.arrow}>→</span>
                            {fieldLabel}
                        </>
                    )}
                    <span className={styles.punctuation}>)</span>
                </span>
            );
        }

        case 'ambient':
            return (
                <span className={styles.value}>
                    <span className={styles.ambientShape} title="Wert außerhalb des geprüften Datensatzes">
                        {operand.shape}
                    </span>
                    {lookupField(operand.path)?.label ?? operand.path}
                </span>
            );

        default: {
            const unknown = operand as { kind: string };
            return <span className={styles.unknown}>Unbekannter Operandentyp: {unknown.kind}</span>;
        }
    }
}
